export interface ColumnRename {
    original: string
    standardized: string
}

// Built at runtime because the TS target (es6) rejects Unicode property escapes in regex literals.
const UNSUPPORTED_CHARS = new RegExp(String.raw`[^\p{L}\p{N}_ ]`, 'gu')

export const hasUnsupportedCharacters = (name: string): boolean => {
    UNSUPPORTED_CHARS.lastIndex = 0
    return UNSUPPORTED_CHARS.test(name)
}

const stripUnsupportedCharacters = (name: string): string => name.replace(UNSUPPORTED_CHARS, '').replaceAll(/\s+/g, ' ').trim()

const UNSUPPORTED_CHAR = new RegExp(String.raw`^[^\p{L}\p{N}_ ]$`, 'u')

export interface ColumnNameSegment {
    text: string
    unsupported: boolean
}

// Groups consecutive supported/unsupported characters so the UI can highlight the unsupported ones.
export function splitByUnsupportedCharacters(name: string): ColumnNameSegment[] {
    const segments: ColumnNameSegment[] = []
    for (const char of Array.from(name)) {
        const unsupported = UNSUPPORTED_CHAR.test(char)
        const last = segments[segments.length - 1]
        if (last?.unsupported === unsupported) last.text += char
        else segments.push({ text: char, unsupported })
    }
    return segments
}

export function getColumnRenames(names: readonly string[]): ColumnRename[] {
    const used = new Set(names.filter((name) => !hasUnsupportedCharacters(name)))
    const renames: ColumnRename[] = []

    names.forEach((name, index) => {
        if (!hasUnsupportedCharacters(name)) return
        const base = stripUnsupportedCharacters(name) || `column_${index + 1}`
        let candidate = base
        for (let suffix = 1; used.has(candidate); suffix++) {
            candidate = `${base}_${suffix}`
        }
        used.add(candidate)
        renames.push({ original: name, standardized: candidate })
    })

    return renames
}
